import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { getSalmos } from "@/lib/salmos";
import { useLang } from "@/lib/i18n";
import SalmoCard from "@/components/SalmoCard";
import OrnamentoDourado from "@/components/OrnamentoDourado";
import SEO from "@/components/SEO";

export default function TodosOsSalmosPage() {
  const todos = useMemo(() => getSalmos(), []);
  const { t } = useLang();

  const tagsUnicas = useMemo(() => {
    const set = new Set();
    todos.forEach((s) => (s.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [todos]);

  const [tagAtiva, setTagAtiva] = useState(null);

  const lista = useMemo(
    () =>
      todos.filter((s) => !tagAtiva || (s.tags || []).includes(tagAtiva)),
    [todos, tagAtiva]
  );

  return (
    <div className="mx-auto max-w-6xl px-6 py-12" data-testid="todos-salmos-page">
      <SEO
        title={`${t.todos_salmos} | salmos.pt`}
        description={t.descricao_lista}
      />

      <nav className="flex items-center gap-2 font-display text-[10px] uppercase tracking-[0.22em] text-[var(--cor-texto-2)]">
        <Link to="/" className="hover:text-[var(--cor-dourado-claro)]">{t.inicio}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[var(--cor-dourado)]">{t.todos_salmos}</span>
      </nav>

      <header className="text-center mt-16">
        <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.antologia_completa}</div>
        <h1 className="font-display text-5xl md:text-6xl text-[var(--cor-texto)] mt-4 font-light">{t.todos_salmos}</h1>
        <p className="font-serif italic text-[var(--cor-texto-2)] mt-6 text-lg max-w-xl mx-auto">
          {t.descricao_lista}
        </p>
        <OrnamentoDourado className="mt-10" />
      </header>

      <div className="mt-16 flex flex-wrap gap-2 justify-center" data-testid="filtros-tags">
        <button
          onClick={() => setTagAtiva(null)}
          className={`font-display text-xs uppercase tracking-[0.22em] px-5 py-2 rounded-full border transition-all ${
            !tagAtiva
              ? "border-[var(--cor-dourado)] text-[var(--cor-dourado-claro)] bg-[var(--cor-dourado)]/10"
              : "border-[var(--cor-borda)] text-[var(--cor-texto-2)] hover:border-[var(--cor-dourado)]/50"
          }`}
          data-testid="filtro-todos"
        >
          {t.todos}
        </button>
        {tagsUnicas.map((tag) => (
          <button
            key={tag}
            onClick={() => setTagAtiva(tag)}
            className={`font-display text-xs uppercase tracking-[0.22em] px-5 py-2 rounded-full border transition-all ${
              tagAtiva === tag
                ? "border-[var(--cor-dourado)] text-[var(--cor-dourado-claro)] bg-[var(--cor-dourado)]/10"
                : "border-[var(--cor-borda)] text-[var(--cor-texto-2)] hover:border-[var(--cor-dourado)]/50"
            }`}
            data-testid={`filtro-tag-${tag}`}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {lista.map((salmo) => <SalmoCard key={salmo.numero} salmo={salmo} />)}
      </div>
    </div>
  );
}
